import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  Query,
  Builder,
  Utils as QbUtils,
  MuiConfig
} from "@react-awesome-query-builder/mui"
import "@react-awesome-query-builder/mui/css/styles.css"
import { ConfirmProvider } from "material-ui-confirm"
import { Box, FormHelperText, Stack } from "@mui/material"
import { useDebouncedCallback } from "use-debounce/lib"
import isEqual from "lodash/isEqual"
import { I18n } from "utils/i18n-shim"
import useLocale from "hooks/useLocale"

const InitialConfig = MuiConfig

const emptyCustomOperators = {}

/**
 * The base query-builder config (i18n labels/widgets), WITHOUT `fields` — callers merge
 * their own the way `<Query>` does internally, so the rule-assistant's draft compiler
 * validates against exactly the rules this widget enforces.
 *
 * `_locale` is unused because every label reads the current locale itself; it exists as
 * a memo key, since `useMemo(() => getConfig(locale), [locale])` below needs the
 * dependency used inside the callback. Callers with no memo pass nothing.
 */
export const getConfig = (_locale, customOperators = emptyCustomOperators) => ({
  ...InitialConfig,
  settings: {
    useConfigCompress: true,
    ...InitialConfig.settings,
    renderProvider: (props, { RCE }) => RCE(MuiProvider, props),
    customOperatorSelectProps: {
      input: {
        variant: "outlined"
      },
      sx: { width: "auto!important" },
      showSearch: true
    },
    customFieldSelectProps: {
      input: {
        variant: "outlined"
      },
      sx: { width: "auto!important" },
      showSearch: true
    },
    valueLabel: I18n.t("query-builder.labels.value-label"),
    valuePlaceholder: I18n.t("query-builder.labels.value-placeholder"),
    fieldLabel: I18n.t("query-builder.labels.field-label"),
    operatorLabel: I18n.t("query-builder.labels.operator-label"),
    funcLabel: I18n.t("query-builder.labels.function-label"),
    fieldPlaceholder: I18n.t("query-builder.labels.field-placeholder"),
    funcPlaceholder: I18n.t("query-builder.labels.function-placeholder"),
    operatorPlaceholder: I18n.t("query-builder.labels.operator-placeholder"),
    lockLabel: I18n.t("query-builder.labels.lock-label"),
    lockedLabel: I18n.t("query-builder.labels.locked-label"),
    deleteLabel: I18n.t("query-builder.labels.delete-label"),
    addGroupLabel: I18n.t("query-builder.labels.add-group-label"),
    addCaseLabel: I18n.t("query-builder.labels.add-case-label"),
    addDefaultCaseLabel: I18n.t("query-builder.labels.add-default"),
    defaultCaseLabel: I18n.t("query-builder.labels.default-case-label"),
    addRuleLabel: I18n.t("query-builder.labels.add-rule-label"),
    addSubRuleLabel: I18n.t("query-builder.labels.add-sub-rule-label"),
    delGroupLabel: I18n.t("query-builder.labels.del-group-label"),
    notLabel: I18n.t("query-builder.labels.not-label"),
    fieldSourcesPopupTitle: I18n.t(
      "query-builder.labels.field-sources-popup-title"
    ),
    valueSourcesPopupTitle: I18n.t(
      "query-builder.labels.value-sources-popup-title"
    )
  },
  conjunctions: {
    ...InitialConfig.conjunctions,
    AND: {
      ...InitialConfig.conjunctions.AND,
      label: I18n.t("query-builder.conjunctions.and")
    },
    OR: {
      ...InitialConfig.conjunctions.OR,
      label: I18n.t("query-builder.conjunctions.or")
    }
  },
  operators: Object.fromEntries(
    Object.entries(InitialConfig.operators).map(([key, value]) => {
      const label = I18n.t(`query-builder.operators.${key}`)

      return [key, { ...value, label, ...customOperators[key] }]
    })
  ),
  widgets: {
    ...InitialConfig.widgets,
    text: {
      ...InitialConfig.widgets.text,
      valuePlaceholder: I18n.t("query-builder.widgets.text.value-placeholder"),
      customProps: {
        variant: "outlined"
      }
    },
    button: {
      ...InitialConfig.widgets.button,
      customProps: {
        variant: "outlined"
      }
    }
  }
})

const MuiProvider = ({ children }) => {
  const base = <div className="mui">{children}</div>
  const withProviders = <ConfirmProvider>{base}</ConfirmProvider>

  return withProviders
}

const CustomBuilder = (props) => {
  return (
    <Box className="query-builder-container">
      <Box
        className="query-builder qb-lite"
        sx={{
          margin: "0 !important",
          "& .group-or-rule-container.group-container:first-of-type": {
            paddingRight: 0
          },
          "&.query-builder .group": {
            background: "#F8F8F8",
            border: "1px solid #DFDFDF"
          },
          "& .rule--body > div": {
            flexGrow: 1,
            maxWidth: "31%",
            "& .MuiFormControl-root": {
              width: "100%"
            },
            "& .rule--widget": {
              width: "100%",
              "& .widget--widget": {
                width: "100%"
              }
            },
            "&.rule--field-wrapper": {
              display: "flex",
              "& .rule--field": {
                width: "100%"
              }
            }
          },
          "& .group--header:not(:hover) .group--actions": {
            opacity: "unset!important"
          },
          "& .rule:not(:hover) .rule--header": {
            opacity: "unset!important"
          },
          "& .rule--header .MuiButtonBase-root": {
            color: "gray"
          }
        }}
      >
        <Builder {...props} />
      </Box>
    </Box>
  )
}

export const QueryBuilder = ({
  fields,
  value,
  onChange,
  error,
  helperText = " ",
  customOperators = emptyCustomOperators
}) => {
  const [tree, setTree] = useState(() => QbUtils.loadTree(value))
  const locale = useLocale()
  const config = useMemo(
    () => getConfig(locale, customOperators),
    [locale, customOperators]
  )

  // Tracks the last plain (compressed) tree WE pushed upstream via onChange, so the
  // reconciliation effect below can tell "value changed because it's our own debounced
  // echo coming back" apart from "value changed externally" (assistant apply, switching
  // to another rule, resetForm). This restores the library's controlled-component
  // contract without a `key` remount, which would tear down expanded-group/focus
  // state and re-fire the async tag/zip field fetches.
  const lastEmittedRef = useRef(value)

  useEffect(() => {
    if (isEqual(value, lastEmittedRef.current)) {
      return
    }

    lastEmittedRef.current = value
    setTree(QbUtils.loadTree(value))
  }, [value])

  const [debouncedHandleChange] = useDebouncedCallback(onChange, 500)

  const handleChange = (tree, config) => {
    setTree(tree)

    if (onChange) {
      const plain = QbUtils.getTree(tree)

      // Recorded synchronously (not in the debounced callback) so that when this same
      // value round-trips back through formik ~500ms later, the effect above recognizes
      // it as our own echo instead of reloading mid-edit.
      lastEmittedRef.current = plain

      debouncedHandleChange({
        tree: plain,
        sql: QbUtils.sqlFormat(tree, config)
      })
    }
  }

  return (
    <Stack>
      <Query
        {...config}
        fields={fields}
        value={tree}
        onChange={handleChange}
        renderBuilder={CustomBuilder}
      />
      <FormHelperText error={error}>{helperText}</FormHelperText>
    </Stack>
  )
}

export default QueryBuilder
